import { Monitor, Moon, Sun } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../hooks/useTheme';
import type { Theme } from '../../lib/theme';

const OPTIONS: { value: Theme; label: string; icon: React.ElementType }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

interface ThemeToggleProps {
  className?: string;
  /** Icon-only variant, useful in tight spaces like the mobile header. */
  compact?: boolean;
}

/**
 * Light / Dark / System switch. The choice is persisted per device in
 * localStorage, so each PC can run its own mode.
 */
export function ThemeToggle({ className, compact = false }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();

  return (
    <div
      role="group"
      aria-label="Color theme"
      className={cn(
        'items-center gap-1 rounded-lg border border-border bg-background p-1',
        compact ? 'inline-flex' : 'grid grid-cols-3',
        className,
      )}
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const isActive = theme === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => setTheme(value)}
            aria-pressed={isActive}
            title={`${label} mode`}
            className={cn(
              'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
              compact ? 'w-8' : 'w-full',
              isActive
                ? 'bg-surface text-primary shadow-sm'
                : 'text-text-muted hover:bg-surface/60 hover:text-text',
            )}
          >
            <Icon size={compact ? 16 : 14} />
            {!compact && <span>{label}</span>}
          </button>
        );
      })}
    </div>
  );
}
